import { ref, watch, type Ref } from 'vue'
import type { ReportDto } from '@/types'

export type ReportType = 'bug' | 'feature' | 'emergency'

export function getDraftKey(type: ReportType): string {
  return `report-draft:${type}`
}

export function hasMeaningfulContent(data: Partial<ReportDto>): boolean {
  if (!data || typeof data !== 'object') return false

  if (data.dataLoss || data.securityImpact || data.productionOutage) {
    return true
  }

  const stringKeys: (keyof ReportDto)[] = [
    'phoneNumber',
    'service',
    'priority',
    'description',
    'observedBehavior',
    'expectedBehavior',
    'stepsToReproduce',
    'businessContext',
    'expectedBenefit',
    'proposedSolution',
    'additionalContext',
    'businessImpact',
    'venaboTicket'
  ]

  return stringKeys.some((k) => {
    const val = data[k]
    return typeof val === 'string' && val.trim().length > 0
  })
}

function toDraftData(data: Partial<ReportDto>): Partial<ReportDto> {
  const draftData: Partial<ReportDto> = { ...data }
  delete draftData.contactPerson
  delete draftData.email
  delete draftData.files
  return draftData
}

export function useReportDraft(type: ReportType, formState: Ref<ReportDto>) {
  const hasDraft = ref<boolean>(false)
  const isPendingRestore = ref<boolean>(false)
  const draftSnapshot = ref<Partial<ReportDto> | null>(null)
  const key = getDraftKey(type)

  function checkDraft() {
    try {
      const stored = localStorage.getItem(key)
      if (stored) {
        const parsed = JSON.parse(stored) as Partial<ReportDto>
        if (hasMeaningfulContent(parsed)) {
          draftSnapshot.value = parsed
          isPendingRestore.value = true
          hasDraft.value = true
          return
        }
      }
    } catch {
      // Ignore corrupt JSON
    }
    draftSnapshot.value = null
    isPendingRestore.value = false
    hasDraft.value = false
  }

  checkDraft()

  function getStoredDraft(): Partial<ReportDto> | null {
    try {
      const stored = localStorage.getItem(key)
      if (stored) {
        const parsed = JSON.parse(stored) as Partial<ReportDto>
        if (hasMeaningfulContent(parsed)) {
          return parsed
        }
      }
    } catch {
      // Ignore
    }
    return null
  }

  function restore() {
    const dataToRestore = draftSnapshot.value || getStoredDraft()
    if (dataToRestore) {
      Object.assign(formState.value, toDraftData(dataToRestore))
    }
    isPendingRestore.value = false
    // Keep hasDraft true only if there was actually data to restore
    if (!dataToRestore) {
      hasDraft.value = false
    }
  }

  function discard() {
    try {
      localStorage.removeItem(key)
    } catch {
      // Ignore
    }
    draftSnapshot.value = null
    isPendingRestore.value = false
    hasDraft.value = false
  }

  function clear() {
    discard()
  }

  let timeoutId: ReturnType<typeof setTimeout> | null = null

  watch(
    formState,
    (val) => {
      if (val.type !== type) return

      const draftData = toDraftData(val)

      // While a restore banner is pending, the stored draft must survive.
      // As soon as the user types something themselves, stop blocking
      // auto-save; the pending draft stays restorable via draftSnapshot.
      if (isPendingRestore.value) {
        if (!hasMeaningfulContent(draftData)) return
        isPendingRestore.value = false
      }

      if (timeoutId) clearTimeout(timeoutId)
      timeoutId = setTimeout(() => {
        try {
          if (hasMeaningfulContent(draftData)) {
            localStorage.setItem(key, JSON.stringify(draftData))
            hasDraft.value = true
          } else {
            localStorage.removeItem(key)
            hasDraft.value = false
          }
        } catch {
          // Ignore quota / localStorage errors
        }
      }, 300)
    },
    { deep: true }
  )

  return {
    hasDraft,
    restore,
    discard,
    clear,
    checkDraft
  }
}
